<template>
    <kt-modal 
        :visible="visible" 
        title="Manage Departments"
        size="modal-lg"
        @dismiss="$emit('close')"
    >
        <template #body>
            <div class="department-selector">
                <!-- Add New Department Form (Admin only) -->
                <div v-if="props.canManage" class="card mb-3">
                    <div class="card-header py-2">
                        <strong>{{ editingDepartment ? 'Edit Department' : 'Add New Department' }}</strong>
                    </div>
                    <div class="card-body py-2">
                        <div class="row g-2">
                            <div :class="hasBillingAccess ? 'col-md-4' : 'col-md-5'">
                                <input
                                    type="text"
                                    class="form-control form-control-sm"
                                    v-model="newDepartment.name"
                                    placeholder="Department Name"
                                />
                            </div>
                            <div :class="hasBillingAccess ? 'col-md-3' : 'col-md-5'">
                                <input
                                    type="email"
                                    class="form-control form-control-sm"
                                    v-model="newDepartment.email"
                                    placeholder="Department Email"
                                />
                            </div>
                            <div v-if="hasBillingAccess" class="col-md-3">
                                <div class="input-group input-group-sm">
                                    <span class="input-group-text">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        class="form-control"
                                        v-model.number="newDepartment.billingRate"
                                        placeholder="Billing Rate"
                                    />
                                </div>
                            </div>
                            <div class="col-md-2">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-primary w-100"
                                    @click="saveDepartment"
                                    :disabled="!newDepartment.name"
                                >
                                    <Save :size="14" class="me-1" />
                                    {{ editingDepartment ? 'Update' : 'Add' }}
                                </button>
                            </div>
                        </div>
                        <div v-if="editingDepartment" class="mt-2">
                            <button 
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="cancelEdit"
                            >
                                Cancel Edit
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Department List -->
                <div class="card">
                    <div class="card-header py-2">
                        <strong>{{ props.manageOnly ? 'Departments' : 'Select a Department' }}</strong>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loading" class="text-center py-3">
                            <span class="spinner-border spinner-border-sm"></span>
                            Loading departments...
                        </div>
                        <div v-else-if="departments.length === 0" class="text-center py-3 text-muted">
                            No departments configured. Add one above.
                        </div>
                        <table v-else class="table table-sm table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>Department Name</th>
                                    <th>Email</th>
                                    <th v-if="hasBillingAccess">Billing Rate</th>
                                    <th style="width: 120px">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="dept in departments"
                                    :key="dept.name"
                                    :class="{ 'table-primary': !props.manageOnly && selectedDepartment === dept.name }"
                                    :style="{ cursor: props.manageOnly ? 'default' : 'pointer' }"
                                    @click="!props.manageOnly && selectDepartment(dept)"
                                >
                                    <td>{{ dept.name }}</td>
                                    <td>
                                        <span class="text-muted">{{ dept.email || '(no email)' }}</span>
                                    </td>
                                    <td v-if="hasBillingAccess">
                                        <span class="text-muted">{{ formatCurrency(dept.billingRate, '(not set)') }}</span>
                                    </td>
                                    <td v-if="props.canManage">
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-primary me-1"
                                            @click.stop="editDepartment(dept)"
                                            title="Edit"
                                        >
                                            <Edit :size="14" />
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click.stop="deleteDepartment(dept)"
                                            title="Delete"
                                        >
                                            <Trash2 :size="14" />
                                        </button>
                                    </td>
                                    <td v-else></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <button type="button" class="btn btn-secondary" @click="$emit('close')">
                {{ props.manageOnly ? 'Close' : 'Cancel' }}
            </button>
            <button
                v-if="!props.manageOnly"
                type="button"
                class="btn btn-primary"
                @click="confirmSelection"
                :disabled="!selectedDepartment"
            >
                Select Department
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import OptionsService from '@/services/OptionsService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { useBilling } from '@/composables/useBilling';
import { Save, Edit, Trash2 } from 'lucide-vue-next';

interface Department {
    name: string;
    email: string;
    billingRate?: number | null;
}

const props = defineProps<{
    visible: boolean;
    currentValue?: string | null;
    canManage?: boolean; // Only Admin users can add/edit/delete departments
    manageOnly?: boolean; // When true, hides the "Select Department" selector UI (admin management view)
}>();

const { hasBillingAccess, formatCurrency } = useBilling();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'select', department: Department): void;
    (e: 'departments-changed', departments: Department[]): void;
}>();

const loading = ref(false);
const departments = ref<Department[]>([]);
const selectedDepartment = ref<string | null>(null);
const editingDepartment = ref<Department | null>(null);
const newDepartment = ref<Department>({ name: '', email: '', billingRate: null });

const OPTION_KEY = 'departments_json';

const loadDepartments = async () => {
    loading.value = true;
    try {
        const optionValue = await OptionsService.get(OPTION_KEY);
        if (optionValue) {
            departments.value = JSON.parse(optionValue);
        } else {
            departments.value = [];
        }
    } catch (error) {
        console.error('Error loading departments:', error);
        departments.value = [];
    } finally {
        loading.value = false;
    }
};

const saveDepartmentsToOptions = async () => {
    try {
        await OptionsService.set(OPTION_KEY, JSON.stringify(departments.value));
        emit('departments-changed', departments.value);
    } catch (error) {
        console.error('Error saving departments:', error);
        Toast.error('Failed to save departments');
    }
};

const saveDepartment = async () => {
    if (!newDepartment.value.name.trim()) {
        Toast.error('Department name is required');
        return;
    }

    if (editingDepartment.value) {
        // Update existing
        const index = departments.value.findIndex(d => d.name === editingDepartment.value!.name);
        if (index !== -1) {
            departments.value[index] = { ...newDepartment.value };
        }
        editingDepartment.value = null;
    } else {
        // Check for duplicate
        if (departments.value.some(d => d.name.toLowerCase() === newDepartment.value.name.toLowerCase())) {
            Toast.error('A department with this name already exists');
            return;
        }
        // Add new
        departments.value.push({ ...newDepartment.value });
    }

    await saveDepartmentsToOptions();
    newDepartment.value = { name: '', email: '', billingRate: null };
    Toast.success('Department saved');
};

const editDepartment = (dept: Department) => {
    editingDepartment.value = dept;
    newDepartment.value = { ...dept };
};

const cancelEdit = () => {
    editingDepartment.value = null;
    newDepartment.value = { name: '', email: '', billingRate: null };
};

const deleteDepartment = async (dept: Department) => {
    Alert.confirm(
        `Are you sure you want to delete "${dept.name}"?`,
        async () => {
            departments.value = departments.value.filter(d => d.name !== dept.name);
            await saveDepartmentsToOptions();
            Toast.success('Department deleted');
            
            if (selectedDepartment.value === dept.name) {
                selectedDepartment.value = null;
            }
        },
        () => {} // Cancel callback
    );
};

const selectDepartment = (dept: Department) => {
    selectedDepartment.value = dept.name;
};

const confirmSelection = () => {
    const dept = departments.value.find(d => d.name === selectedDepartment.value);
    if (dept) {
        emit('select', dept);
    }
    emit('close');
};

watch(() => props.visible, (newVal) => {
    if (newVal) {
        loadDepartments();
        selectedDepartment.value = props.currentValue || null;
    }
});

onMounted(() => {
    if (props.visible) {
        loadDepartments();
        selectedDepartment.value = props.currentValue || null;
    }
});
</script>

<style scoped>
.department-selector {
    min-height: 300px;
}
</style>
